import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Upload,
    Link as LinkIcon,
    X,
    Loader2,
    Image as ImageIcon,
    Images,
    RefreshCw,
    Search,
    Check,
} from "lucide-react";
import { API } from "../../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function resolveSrc(url) {
    if (!url) return "";
    if (/^https?:/i.test(url)) return url;
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
}

function formatSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImageInput({
    value,
    onChange,
    label = "Imagem",
    testid = "image-input",
}) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("url");
    const [gallery, setGallery] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [galleryLoaded, setGalleryLoaded] = useState(false);
    const [search, setSearch] = useState("");

    const token = localStorage.getItem("sabor-liquido-admin-token");

    const fetchGallery = async () => {
        setGalleryLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/uploads`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setGallery(data.items || []);
            setGalleryLoaded(true);
        } catch (err) {
            toast.error(
                err.response?.data?.detail || "Erro a carregar galeria"
            );
        } finally {
            setGalleryLoading(false);
        }
    };

    useEffect(() => {
        if (mode === "gallery" && !galleryLoaded) {
            fetchGallery();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Ficheiro demasiado grande (máx 5 MB)");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/uploads`, fd, {
                headers: { Authorization: `Bearer ${token}` },
            });
            onChange(data.url);
            toast.success("Imagem carregada");
            // Invalidate gallery cache so next open is fresh
            setGalleryLoaded(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao fazer upload");
        } finally {
            setBusy(false);
        }
    };

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (f) upload(f);
    };

    const filtered = search.trim()
        ? gallery.filter((it) =>
              it.filename.toLowerCase().includes(search.toLowerCase())
          )
        : gallery;

    const ModeBtn = ({ id, icon: Icon, children }) => (
        <button
            type="button"
            onClick={() => setMode(id)}
            className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2.5 py-1 transition-colors ${
                mode === id
                    ? "bg-brand-green text-white"
                    : "bg-brand-cream text-brand-ink-soft hover:bg-brand-green-soft"
            }`}
            data-testid={`${testid}-mode-${id}`}
        >
            <Icon className="h-3 w-3 inline mr-1" />
            {children}
        </button>
    );

    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft mb-1.5">
                {label}
            </div>
            <div className="flex items-stretch gap-2 flex-wrap">
                {value ? (
                    <div className="relative h-20 w-20 rounded-2xl bg-brand-cream overflow-hidden ring-1 ring-brand-green/15 flex items-center justify-center flex-shrink-0">
                        <img
                            src={resolveSrc(value)}
                            alt="preview"
                            className="h-full w-full object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            data-testid={`${testid}-clear`}
                            className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white hover:bg-red-500 transition-colors flex items-center justify-center"
                            aria-label="Remover"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                ) : (
                    <div className="h-20 w-20 rounded-2xl bg-brand-cream ring-1 ring-brand-green/15 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="h-6 w-6 text-brand-ink-soft" />
                    </div>
                )}
                <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                        <ModeBtn id="url" icon={LinkIcon}>URL</ModeBtn>
                        <ModeBtn id="upload" icon={Upload}>Upload</ModeBtn>
                        <ModeBtn id="gallery" icon={Images}>Galeria</ModeBtn>
                    </div>

                    {mode === "url" && (
                        <input
                            type="text"
                            value={value || ""}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="https://... ou /caminho/imagem.png"
                            data-testid={`${testid}-url-field`}
                            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-3 py-2.5 text-xs text-brand-ink focus:outline-none focus:border-brand-green"
                        />
                    )}

                    {mode === "upload" && (
                        <div>
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={busy}
                                data-testid={`${testid}-upload-btn`}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-green/30 bg-brand-cream/60 hover:bg-brand-green-soft px-3 py-2.5 text-xs font-semibold text-brand-ink transition-colors disabled:opacity-60"
                            >
                                {busy ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Upload className="h-3.5 w-3.5" />
                                )}
                                {busy ? "A carregar..." : "Escolher ficheiro (máx 5 MB)"}
                            </button>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFile}
                                data-testid={`${testid}-upload-file`}
                                className="hidden"
                            />
                        </div>
                    )}

                    {mode === "gallery" && (
                        <div
                            className="rounded-2xl border-2 border-brand-green/15 bg-white p-2.5"
                            data-testid={`${testid}-gallery`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-brand-ink-soft" />
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Procurar por nome..."
                                        data-testid={`${testid}-gallery-search`}
                                        className="w-full rounded-full border border-brand-green/15 bg-brand-cream/60 pl-7 pr-3 py-1.5 text-xs text-brand-ink focus:outline-none focus:border-brand-green"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={fetchGallery}
                                    disabled={galleryLoading}
                                    data-testid={`${testid}-gallery-refresh`}
                                    className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink-soft transition-colors disabled:opacity-60"
                                    aria-label="Actualizar galeria"
                                >
                                    {galleryLoading ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-3.5 w-3.5" />
                                    )}
                                </button>
                            </div>

                            {galleryLoading && !galleryLoaded ? (
                                <div className="text-xs text-brand-ink-soft py-6 text-center">
                                    A carregar imagens...
                                </div>
                            ) : filtered.length === 0 ? (
                                <div
                                    className="text-xs text-brand-ink-soft py-6 text-center"
                                    data-testid={`${testid}-gallery-empty`}
                                >
                                    {gallery.length === 0
                                        ? "Sem imagens carregadas. Usa o separador 'Upload' para começar."
                                        : "Nenhuma imagem corresponde à pesquisa."}
                                </div>
                            ) : (
                                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 max-h-60 overflow-y-auto pr-1">
                                    {filtered.map((it) => {
                                        const selected = value === it.url;
                                        return (
                                            <button
                                                key={it.filename}
                                                type="button"
                                                onClick={() => onChange(it.url)}
                                                data-testid={`${testid}-gallery-item-${it.filename}`}
                                                title={`${it.filename} • ${formatSize(it.size)}`}
                                                className={`relative aspect-square rounded-xl overflow-hidden ring-2 transition-all ${
                                                    selected
                                                        ? "ring-brand-green"
                                                        : "ring-transparent hover:ring-brand-green/40"
                                                }`}
                                            >
                                                <img
                                                    src={resolveSrc(it.url)}
                                                    alt={it.filename}
                                                    loading="lazy"
                                                    className="h-full w-full object-cover bg-brand-cream"
                                                />
                                                {selected && (
                                                    <div className="absolute inset-0 bg-brand-green/30 flex items-center justify-center">
                                                        <div className="h-6 w-6 rounded-full bg-brand-green text-white flex items-center justify-center">
                                                            <Check className="h-3.5 w-3.5" />
                                                        </div>
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {gallery.length > 0 && (
                                <div className="text-[10px] text-brand-ink-soft mt-2 text-right">
                                    {filtered.length} de {gallery.length} imagens
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
